<template>
  <div class="panel" data-qid="engagement-project-settings">
    <header class="panel__header">
      <div class="profile-compact">
        <div class="logo-container">
          <font-awesome-icon icon="cog" class="icon-svg--sm"/>
        </div>
        <div class="profile-compact__info">
          <div class="profile-compact__top">
            <div class="profile-compact__text">
              {{ $t('dashboard.settings') }}
            </div>
          </div>
        </div>
      </div>

      <div class="profile-compact__actions">
        <span class="icon-btn"
              v-if="canEdit && !editing"
              @click="startEditing"
              data-qid="engagement-settings-edit-button"
        >
          <font-awesome-icon icon="pen"/>
        </span>
        <span class="icon-btn btn-delete"
              v-if="editing"
              @click="editing = false"
              data-qid="engagement-settings-cancel-button"
        >
          <font-awesome-icon icon="rotate-left"/>
        </span>
        <span class="icon-btn btn-edit"
              v-if="editing"
              @click="save"
              data-qid="engagement-settings-save-button"
        >
          <font-awesome-icon icon="save"/>
        </span>
      </div>
    </header>

    <div class="panel__body">
      <!-- Fixed when the engagement is created -->
      <div class="engagement-fixed-settings" data-qid="engagement-settings-fixed">
        <div class="engagement-fixed-settings__hint">
          <font-awesome-icon icon="lock" class="mr-1"/>{{ $t('projects.engagement.settings.fixed_hint') }}
        </div>
        <div class="engagement-facts mb-0">
          <div class="engagement-facts__item" v-if="project.assessment">
            <span class="engagement-facts__label">{{ $t('table.template') }}</span>
            <span>{{ project.assessment.name }}</span>
          </div>
          <div class="engagement-facts__item">
            <span class="engagement-facts__label">{{ $t('projects.create.schedule_label') }}</span>
            <span v-if="project.project">{{ $t('projects.schedule.' + $options.filters.slugify(project.schedule_type)) }}</span>
            <span v-else>{{ $t('projects.schedule.one_time') }}</span>
          </div>
        </div>
      </div>

      <!-- Period or point-in-time: shown here, changed with the scope on the Engagement acceptance tab -->
      <div class="engagement-audit-type" v-if="project.type === 'control_assessment'" data-qid="engagement-settings-audit-type">
        <div class="engagement-audit-type__main">
          <div class="engagement-facts mb-0">
            <div class="engagement-facts__item">
              <span class="engagement-facts__label">{{ $t('projects.create.project_type') }}</span>
              <b v-if="project.project_type">{{ project.project_type === 'type two' ? $t('system.type_two') : $t('system.type_one') }}</b>
              <span v-else class="text-muted">{{ $t('system.missing_input') }}</span>
            </div>
            <div class="engagement-facts__item" v-if="project.scope_start_date">
              <span class="engagement-facts__label">{{ project.project_type === 'type two' ? $t('projects.view.scope_period_title') : $t('system.point_in_time') }}</span>
              <span v-if="project.project_type === 'type two' && project.scope_end_date">
                {{ project.scope_start_date | formatDate('DD MMM YYYY', true) }} – {{ project.scope_end_date | formatDate('DD MMM YYYY', true) }}
              </span>
              <span v-else>{{ project.scope_start_date | formatDate('DD MMM YYYY', true) }}</span>
            </div>
          </div>
          <div class="engagement-audit-type__hint">{{ $t('projects.engagement.settings.audit_type_hint') }}</div>
        </div>
        <b-button size="sm"
                  variant="secondary"
                  @click="$emit('open-tab', 'acceptance')"
                  data-qid="engagement-settings-audit-type-open"
        >
          {{ $t('projects.engagement.settings.audit_type_change') }}<font-awesome-icon icon="arrow-right" class="ml-1"/>
        </b-button>
      </div>

      <div class="engagement-facts">
        <div class="engagement-facts__item">
          <span class="engagement-facts__label">{{ $t('table.last_updated') }}</span>
          <span>{{ project.updated_at | formatDate }}</span>
          <span v-if="project.updated_by" class="text-muted">· {{ project.updated_by.name }}</span>
        </div>
        <div class="engagement-facts__item" v-if="project.tags_translated && project.tags_translated.length">
          <span class="engagement-facts__label">{{ $t('table.tags') }}</span>
          <span class="badge" v-for="(tag, index) in project.tags_translated" :key="'tag-' + index">
            <font-awesome-icon icon="tag"/> {{ tag.name_translated | truncate(35) }}
          </span>
        </div>
      </div>

      <b-alert v-if="error.length"
               show
               variant="danger"
               class="mt-2"
      >{{ error }}</b-alert>

      <!-- Editable settings -->
      <div class="engagement-settings-list">
        <div class="engagement-settings-list__row">
          <label>{{ $t('projects.create.reporting_language') }}</label>
          <multiselect v-if="editing"
                       v-model="form.language"
                       :options="languages"
                       select-label=""
                       selected-label=""
                       deselect-label=""
                       label="text"
                       track-by="value"
                       :placeholder="$t('processes.create.language_placeholder')"
          ></multiselect>
          <span v-else-if="currentLanguage">{{ currentLanguage.text }}</span>
          <span v-else class="text-muted">{{ $t('system.missing_input') }}</span>
        </div>

        <div class="engagement-settings-list__row" v-for="field in yesNoFields" :key="field.key">
          <label>{{ $t(field.label) }}</label>
          <b-form-radio-group v-if="editing" v-model="form[field.key]" :options="yesNoOptions"></b-form-radio-group>
          <span v-else :class="project[field.key] == 1 ? 'engagement-setting-on' : 'engagement-setting-off'">
            <font-awesome-icon :icon="project[field.key] == 1 ? 'check-circle' : 'minus-circle'" class="mr-1"/>
            {{ project[field.key] == 1 ? $t('system.yes') : $t('system.no') }}
          </span>
        </div>

        <div class="engagement-settings-list__row">
          <label>{{ $t('system.required_sign_offs') }}</label>
          <div v-if="editing">
            <NumberStepper v-model="form.required_sign_offs" qid="engagement-settings-required-sign-offs"></NumberStepper>
            <small class="form-text text-muted">{{ $t('system.required_sign_offs_description') }}</small>
          </div>
          <span v-else>{{ project.required_sign_offs ? project.required_sign_offs : 0 }}</span>
        </div>

        <div class="engagement-settings-list__row">
          <label>{{ $t('projects.create.external_link_label') }}</label>
          <multiselect v-if="editing"
                       v-model="form.external_client"
                       :options="clients"
                       :internal-search="false"
                       @search-change="loadClients"
                       select-label=""
                       selected-label=""
                       :deselect-label="$t('projects.create.external_link_remove_button')"
                       label="name"
                       track-by="slug"
                       :placeholder="$t('projects.create.external_link_label')"
          >
            <template slot="singleLabel" slot-scope="props">
              <Avatar :object="props.option" size="xs"></Avatar>
              <span class="ml-2">{{ props.option.name }}</span>
            </template>
            <template slot="option" slot-scope="props">
              <Avatar :object="props.option" size="xs"></Avatar>
              <span class="ml-2">{{ props.option.name }}</span>
            </template>
          </multiselect>
          <span v-else-if="linkedClient">
            <Avatar :object="linkedClient" size="xs"></Avatar>
            <span class="ml-1">{{ linkedClient.name }}</span>
          </span>
          <span v-else class="text-muted">{{ $t('system.n_a') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Avatar from '@/components/Avatar.vue';
import NumberStepper from '@/components/NumberStepper.vue';
import { engagementPermissions } from '@/views/Projects/Engagement/engagement.js';

// Settings of an engagement: fixed parameters and the settings Managers can change
export default {
  name: 'EngagementProjectSettings',
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    Avatar,
    NumberStepper
  },
  data: function() {
    return {
      editing: false,
      error: '',
      form: {},
      clients: [],
      yesNoOptions: [
        { text: this.$t('system.yes'), value: 1 },
        { text: this.$t('system.no'), value: 0 }
      ],
      yesNoFields: [
        { key: 'include_relations', label: 'projects.create.include_relations' },
        { key: 'include_mapping', label: 'maintenance.assessments.include_mapping_title' },
        { key: 'include_independence_declaration', label: 'maintenance.assessments.require_independence_declaration_title' },
        { key: 'require_engagement_acceptance', label: 'maintenance.assessments.require_engagement_acceptance_title' },
        { key: 'include_budget', label: 'projects.engagement.settings.include_budget' },
        { key: 'include_observations', label: 'projects.engagement.settings.include_observations' }
      ],
      languages: [
        {value: 'en', text: 'English'},
        {value: 'da', text: 'Danish'},
        {value: 'fr', text: 'French'},
        {value: 'de', text: 'German'},
        {value: 'sv', text: 'Swedish'},
        {value: 'nl', text: 'Dutch'},
        {value: 'es', text: 'Spanish'}
      ]
    }
  },
  computed: {
    getUser() {
      return this.$store.state.user;
    },
    canEdit() {
      return !!engagementPermissions(this.project).manage && this.project.status !== 'closed' && this.project.status !== 'completed';
    },
    // Seen from the linked client, the link points back to the client that owns the engagement
    linkedClient() {
      const client = this.project.additional_client;
      if (!client || !client.slug) {
        return null;
      }
      const currentClient = this.$store.state.client;
      return currentClient && currentClient.slug === client.slug && this.project.active_client ? this.project.active_client : client;
    },
    currentLanguage() {
      return _.find(this.languages, language => language.value === this.project.language) || null;
    }
  },
  methods: {
    startEditing() {
      this.error = '';
      this.form = {
        language: this.currentLanguage,
        include_relations: this.project.include_relations ? 1 : 0,
        include_mapping: this.project.include_mapping ? 1 : 0,
        include_independence_declaration: this.project.include_independence_declaration ? 1 : 0,
        require_engagement_acceptance: this.project.require_engagement_acceptance === false || this.project.require_engagement_acceptance === 0 ? 0 : 1,
        include_budget: this.project.include_budget ? 1 : 0,
        include_observations: this.project.include_observations ? 1 : 0,
        required_sign_offs: this.project.required_sign_offs ? this.project.required_sign_offs : 0,
        external_client: this.project.additional_client && this.project.additional_client.slug ? this.project.additional_client : null
      };
      this.loadClients();
      this.editing = true;
    },
    loadClients(query) {
      let self = this;
      piincHttp.get('clients', { params: {
        per_page: 100,
        without_partition: 1,
        member: this.getUser && !this.getUser.isAdmin ? this.getUser.id : undefined,
        keyword: query ? query : undefined
      }}).then(function(response) {
        self.clients = response.data.items;
      }, function() {});
    },
    save() {
      let self = this;
      piincHttp.put('projects/' + this.project.slug, {
        language: this.form.language ? this.form.language.value : null,
        include_relations: this.form.include_relations,
        include_mapping: this.form.include_mapping,
        include_independence_declaration: this.form.include_independence_declaration,
        require_engagement_acceptance: this.form.require_engagement_acceptance,
        include_budget: this.form.include_budget,
        include_observations: this.form.include_observations,
        required_sign_offs: this.form.required_sign_offs,
        external_client: this.form.external_client ? this.form.external_client.slug : ''
      }).then(function() {
        self.editing = false;
        self.$toastr('success', self.$t('projects.update.success'));
        self.$emit('updated');
      }, function(error) {
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    }
  }
}
</script>
